<template>
  <ul class="m-0" @dragover="handleDragOver">
    <li class="dropdown-item-container">
      <EditorDeckSlidesThreeDCollapsibleGroup
        v-if="dataSetGroups.length"
        title="Drilling Data"
        :disabled="disabled"
        :is-collapsed="isAllCollapsed"
        @click.alt="toggleCollapse"
      >
        <template #icon>
          <FontAwesomeIcon class="mr-2" icon="folder" size="xs" />
        </template>

        <template #visibility><span /></template>

        <template #default>
          <EditorDeckSlidesThreeDCollapsibleGroup
            v-for="group in dataSetGroups"
            :key="group.id"
            :data-group-id="group.id"
            :title="group.name"
            :disabled="disabled"
            type="dataset"
            :selected="!!(selectedDrillLayerGroup && group.type === 'drill' && selectedDrillLayerGroup.data_set_id === group.dataSetId)"
            :is-collapsed="isAllCollapsed"
            @collapsible-header-click="$emit('collapsible-header-click', group)"
            @contextmenu="handleDrillGroupContextmenu($event, group)"
            @click.alt="toggleCollapse"
          >
            <template #icon>
              <FontAwesomeIcon class="mr-2" icon="database" size="xs" />
            </template>

            <template #visibility>
              <button class="appearance-none w-full h-full" @click="handleDrillGroupVisibility(group)">
                <EditorDeckSlidesThreeDVisibilityIcon :visible="group.someVisible" :class="{ 'opacity-60': !group.someVisible }" />
              </button>
            </template>

            <template #default>
              <EditorDeckSlidesThreeDDrillLayer
                :layers="group.layers as DrillLayer[]"
                :readonly="readonly"
                :type="group.type"
                :disabled="disabled"
                :selected-layers="selectedLayers as DrillLabelLayer[]"
                :labels-manager="labelsManager"
                :scoping-element="scopingElement"
                :selected-label-id="selectedLabelId"
                :is-editing-individual-scene="isEditingIndividualScene"
                :selected-legend="selectedLegend"
                @set-layer-visible="$emit('set-layer-visible', $event)"
                @click="forwardClick"
                @contextmenu="forwardContextmenu"
              />
            </template>
          </EditorDeckSlidesThreeDCollapsibleGroup>
        </template>
      </EditorDeckSlidesThreeDCollapsibleGroup>
    </li>
    <li
      v-for="group in groups"
      :key="group.id"
      class="dropdown-item-container"
      :data-group-id="group.id"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent
      @dragleave="handleDragLeave"
      @dragend.prevent="handleDragEnd"
      @drop.prevent="handleDrop"
    >
      <!-- Only render groups if there's more than one group -->
      <!-- @vue-ignore -->
      <template v-if="layerGroups.length === 1 && layerGroups[0].isDefault">
        <!-- Drill data will never be included with 0 other layer groups -->
        <!-- @vue-ignore -->
        <DeckLayers
          :layers="group.layers as LabelLayer[]"
          :type="group.type"
          :readonly="readonly"
          :disabled="disabled"
          :selected-layers="selectedLayers"
          :labels-manager="labelsManager"
          :scoping-element="scopingElement"
          :selected-label-id="selectedLabelId"
          :is-editing-individual-scene="isEditingIndividualScene"
          @set-layer-visible="$emit('set-layer-visible', $event)"
          @layer-hover="$emit('layer-hover', $event)"
          @click="forwardClick"
          @contextmenu="forwardContextmenu"
          @vrify-dragstart="handleDragStart"
        />
      </template>
      <EditorDeckSlidesThreeDCollapsibleGroup
        v-else
        :title="group.name"
        :visible="group.allVisible"
        :disabled="disabled"
        :is-collapsed="isAllCollapsed"
        @contextmenu="$emit('contextmenu', $event, { ...group, categoryName: group.name })"
        @collapsible-header-click="$emit('collapsible-header-click', group)"
        @click.alt="toggleCollapse"
      >
        <template #icon>
          <FontAwesomeIcon class="mr-2" icon="folder" size="xs" />
        </template>

        <template #visibility><span /></template>

        <DeckLayers
          :layers="group.layers as LabelLayer[]"
          :type="group.type"
          :readonly="readonly"
          :disabled="disabled"
          :selected-layers="selectedLayers"
          :labels-manager="labelsManager"
          :scoping-element="scopingElement"
          :selected-label-id="selectedLabelId"
          :is-editing-individual-scene="isEditingIndividualScene"
          @set-layer-visible="$emit('set-layer-visible', $event)"
          @layer-hover="$emit('layer-hover', $event)"
          @click="forwardClick"
          @contextmenu="forwardContextmenu"
          @vrify-dragstart="handleDragStart"
        />
      </EditorDeckSlidesThreeDCollapsibleGroup>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import type { SceneElement } from '@/entities/model3d'
import type { LabelsManager } from '@/lib/three-d-resources/labels-manager'
import type { DrillDataSet } from '@/entities/drillLabel'
import type { LayerGroup, UseThreeDViewer } from '~/composables/useThreeDViewer'
import type { ThreeDElement } from '~/composables/useLayerSelect'
import type { DrillDataSetLayerGroup } from '~/composables/useDrillDataSetGroups'
import type { DrillLayer, DrillLabelLayer } from '~/entities/drillLabel'
import type { Option } from '~/components/VDropdown.vue'

import { throttle } from '~/lib/utils'
import { toggleElementVisibility, isDrillDataSetGroup } from '~/lib/three-d-editor/utils'

const props = defineProps({
  layerGroups: {
    type: Array as PropType<LayerGroup[]>,
    required: true,
  },
  selectedLayers: {
    type: Array as PropType<DeepReadonly<ThreeDElement[]>>,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  labelsManager: {
    type: Object as PropType<LabelsManager>,
    required: true,
  },
  scopingElement: {
    type: Object as PropType<SceneElement | null>,
    default: null,
  },
  isEditingIndividualScene: {
    type: Boolean,
    default: false,
  },
  selectedLabelId: {
    type: String as PropType<string | null>,
    default: null,
  },
  selectedDrillLayerGroup: {
    type: Object as PropType<DrillDataSet | undefined>,
    default: undefined,
  },
  selectedLegend: {
    type: Object as PropType<Option | null>,
    default: undefined,
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
})

const emit = defineEmits(['set-layer-visible', 'layer-hover', 'click', 'contextmenu', 'collapsible-header-click'])

const dragging = ref<string | null>(null)
const targetGroup = ref<number | null>(null)

const groups = computed(() => {
  const groupsWithoutDataSets = props.layerGroups.filter(group => !(group.type === 'drill'))
  return groupsWithoutDataSets
})

const dataSetGroups = computed(() => {
  return props.layerGroups.filter(group => group.type === 'drill') as DrillDataSetLayerGroup[]
})

const isAllCollapsed = ref(false)

function toggleCollapse() {
  isAllCollapsed.value = !isAllCollapsed.value
}

function handleDrillGroupContextmenu(e: MouseEvent, group: DrillDataSetLayerGroup) {
  emit('contextmenu', e, { ...group, categoryName: group.name })
}

function handleDrillGroupVisibility(group: DrillDataSetLayerGroup) {
  const elements = group.layers.map(val => (!isDrillDataSetGroup(val) ? [val] : val.files.flatMap(f => f.labels))).flat()
  toggleElementVisibility({ elements, threeDViewer: props.threeDViewer })
}

function _forward(type: Parameters<typeof emit>[0]) {
  return function (e: Event, ...args: any[]): void {
    emit(type, e, ...args)
  }
}

function _toggleDragOverClass(e: DragEvent) {
  const target = e.target as HTMLElement
  const parentElement = target.parentElement?.closest('.dropdown-item-container') as HTMLElement
  parentElement.classList.toggle('bg-green-500')
}

function handleDragEnter(e: DragEvent) {
  const tg = (e.target as HTMLElement)?.closest('li[data-group-id]')?.getAttribute('data-group-id')
  targetGroup.value = +(tg ?? 0)
  _toggleDragOverClass(e)
}

function handleDragStart(e: string) {
  dragging.value = e
}

function handleDragLeave(e: DragEvent) {
  _toggleDragOverClass(e)
}

const scrollThreshold = 250
const scrollSpeed = 10

let scrollInterval: NodeJS.Timeout

function handleDragEnd() {
  props.labelsManager.updateLabel(dragging.value!, { nodeId: targetGroup.value ?? 0 })
  clearInterval(scrollInterval)
}

function handleDrop(e: DragEvent) {
  _toggleDragOverClass(e)
  clearInterval(scrollInterval)
}

const handleDragOver = throttle((event: DragEvent) => {
  event.preventDefault()

  const listContainer = document.querySelector('._3d-layers-list')!
  const containerRect = listContainer.getBoundingClientRect()

  if (event.clientY < containerRect.top + scrollThreshold) {
    clearInterval(scrollInterval)
    scrollInterval = setInterval(() => {
      listContainer.scrollTop -= scrollSpeed
    }, 10)
  } else if (event.clientY > containerRect.bottom - scrollThreshold) {
    clearInterval(scrollInterval)
    scrollInterval = setInterval(() => {
      listContainer.scrollTop += scrollSpeed
    }, 10)
  } else {
    clearInterval(scrollInterval)
  }
}, 250)

const forwardClick = _forward('click')
const forwardContextmenu = _forward('contextmenu')
</script>

<style lang="stylus" scoped>
.layer-title
  @apply cursor-default select-none m-0 ml-6
  // These 4 for for no text wrap and ellipsis overflow
  @apply min-w-0 whitespace-nowrap overflow-hidden overflow-ellipsis

.dropdown-item-container:last-child
  @apply border-gray-700 border-b
</style>
